import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Printer, Plus, Minus, Trash2, FileSearch } from "lucide-react";
import { toast } from "sonner";

export default function BarcodeLabels() {
  const { activeBranchId } = useAuth();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);  // {product_id, name, barcode, count}
  const [loading, setLoading] = useState(false);
  // Issue 1b — print all items from a purchase bill (by Bill #)
  const [billSearch, setBillSearch] = useState("");
  const [billLoading, setBillLoading] = useState(false);
  const [matchedBill, setMatchedBill] = useState(null);  // {id, bill_number, items_count}

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);
  const branchProducts = products.filter((p) => p.branch_id === activeBranchId);
  const filtered = branchProducts.filter((p) => [p.name, p.barcode].some((v) => (v || "").toLowerCase().includes(search.toLowerCase()))).slice(0, 20);

  const add = (p) => {
    if (selected.find((x) => x.product_id === p.id)) return;
    setSelected([...selected, { product_id: p.id, name: p.name, barcode: p.barcode, count: 5 }]);
    setSearch("");
  };
  const update = (id, count) => setSelected(selected.map((x) => x.product_id === id ? { ...x, count: Math.max(1, count) } : x));
  const remove = (id) => setSelected(selected.filter((x) => x.product_id !== id));

  const generate = async () => {
    if (selected.length === 0) return;
    setLoading(true);
    try {
      const res = await api.post("/barcode-labels", { items: selected.map((s) => ({ product_id: s.product_id, count: s.count })) }, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      toast.success("PDF generated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  // ---- Print-from-Bill mode (Issue 1b) ----
  // Find the bill by bill_number, then either load its items into the selection table
  // (for tweaking counts) or render labels straight away via the server expand.
  const lookupBill = async () => {
    const q = billSearch.trim();
    if (!q) return;
    setBillLoading(true);
    setMatchedBill(null);
    try {
      const r = await api.get("/purchase-bills", { params: { search: q, page: 1, page_size: 10 } });
      const rows = r.data?.rows || [];
      const exact = rows.find((b) => (b.bill_number || "").toLowerCase() === q.toLowerCase());
      const bill = exact || rows[0];
      if (!bill) {
        toast.error("No purchase bill matches that number");
        return;
      }
      setMatchedBill({ id: bill.id, bill_number: bill.bill_number, party_name: bill.party_name, items_count: (bill.items || []).length });
      // Pre-fill selection with each linked product (count = bill qty)
      const newRows = (bill.items || [])
        .filter((it) => it.product_id)
        .map((it) => ({ product_id: it.product_id, name: it.name, barcode: it.barcode || "", count: Math.max(1, parseInt(it.quantity) || 1) }));
      if (newRows.length === 0) {
        toast.warning("This bill has no items linked to products yet — try posting the bill first.");
        return;
      }
      // Merge — if an item is already selected, sum counts; otherwise append.
      const merged = [...selected];
      newRows.forEach((nr) => {
        const idx = merged.findIndex((x) => x.product_id === nr.product_id);
        if (idx >= 0) merged[idx] = { ...merged[idx], count: merged[idx].count + nr.count };
        else merged.push(nr);
      });
      setSelected(merged);
      toast.success(`Added ${newRows.length} items from bill ${bill.bill_number} — adjust counts and click Generate PDF.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBillLoading(false);
    }
  };

  // One-click direct render (no selection step) — uses the new purchase_bill_id mode.
  const directPrintBill = async () => {
    if (!matchedBill) return;
    setBillLoading(true);
    try {
      const r = await api.post("/barcode-labels", { purchase_bill_id: matchedBill.id }, { responseType: "blob" });
      const skipped = parseInt(r.headers["x-labels-skipped"] || "0", 10);
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success(skipped ? `Labels ready (${skipped} items skipped — no linked product).` : "Bill barcode labels ready.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBillLoading(false);
    }
  };

  const totalLabels = selected.reduce((a, s) => a + s.count, 0);

  return (
    <div className="space-y-6 max-w-3xl" data-testid="barcode-labels-page">
      {/* Issue 1b — bulk print labels for an entire purchase bill */}
      <Card className="rounded-sm p-6 border-slate-200" data-testid="bill-labels-card">
        <h3 className="text-base font-bold tracking-tight mb-1 flex items-center gap-2" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }}>
          <FileSearch className="w-4 h-4" />Print all labels from a purchase bill
        </h3>
        <p className="text-sm text-slate-500 mb-3">Look up a posted purchase bill by its number — we'll either bulk-print every item in one click, or load them below for fine-tuning.</p>
        <div className="flex gap-2">
          <Input placeholder="Bill number (e.g. INV-2025-001)" value={billSearch} onChange={(e) => setBillSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupBill(); } }} className="rounded-none" data-testid="bill-number-search" />
          <Button onClick={lookupBill} disabled={billLoading || !billSearch.trim()} className="rounded-none bg-slate-900 hover:bg-slate-800" data-testid="bill-lookup-btn">{billLoading ? "..." : "Look up"}</Button>
        </div>
        {matchedBill && (
          <div className="mt-3 border border-violet-200 bg-violet-50/60 p-3 flex flex-wrap items-center justify-between gap-2" data-testid="matched-bill-info">
            <div className="text-sm">
              <div><span className="text-slate-500">Bill #</span> <b className="font-mono">{matchedBill.bill_number}</b> · <span className="text-slate-500">Party:</span> <b>{matchedBill.party_name || "—"}</b> · <span className="text-slate-500">Items:</span> <b>{matchedBill.items_count}</b></div>
              <div className="text-xs text-slate-600 mt-1">Loaded into the selection table below — or print everything in one click using its actual quantities.</div>
            </div>
            <Button onClick={directPrintBill} disabled={billLoading} className="rounded-none bg-violet-600 hover:bg-violet-700 text-white" data-testid="bill-direct-print-btn"><Printer className="w-4 h-4 mr-2" />Print bill labels now</Button>
          </div>
        )}
      </Card>

      <Card className="rounded-sm p-6 border-slate-200">
        <h2 className="text-xl font-bold tracking-tight mb-1" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }}>Barcode Label Sheets</h2>
        <p className="text-sm text-slate-500 mb-4">Select products and label counts, then generate printable PDF (Code-128 barcodes, layout from Label Builder).</p>
        <div className="relative mb-4">
          <Input placeholder="Search product to add..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none" data-testid="barcode-search" />
          {search && filtered.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 shadow-lg z-20 max-h-64 overflow-auto">
              {filtered.map((p) => (
                <button key={p.id} onClick={() => add(p)} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 flex justify-between text-sm" data-testid={`barcode-add-${p.id}`}>
                  <span>{p.name}</span><span className="font-mono text-xs text-slate-500">{p.barcode}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200"><tr><th className="p-2 text-left">Product</th><th className="p-2 text-left">Barcode</th><th className="p-2 text-center">Labels</th><th /></tr></thead>
            <tbody>
              {selected.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-slate-400">Add products to print labels.</td></tr>}
              {selected.map((s) => (
                <tr key={s.product_id} className="border-b border-slate-100" data-testid={`barcode-row-${s.product_id}`}>
                  <td className="p-2">{s.name}</td>
                  <td className="p-2 font-mono text-xs">{s.barcode}</td>
                  <td className="p-2">
                    <div className="flex items-center justify-center gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7 rounded-none" onClick={() => update(s.product_id, s.count - 1)}><Minus className="w-3 h-3" /></Button>
                      <span className="w-12 text-center tabular-nums">{s.count}</span>
                      <Button size="icon" variant="ghost" className="h-7 w-7 rounded-none" onClick={() => update(s.product_id, s.count + 1)}><Plus className="w-3 h-3" /></Button>
                    </div>
                  </td>
                  <td className="p-2 text-right"><Button size="icon" variant="ghost" className="h-7 w-7 rounded-none text-red-600" onClick={() => remove(s.product_id)}><Trash2 className="w-3 h-3" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex justify-between items-center">
          <div className="text-sm text-slate-600">Total labels: <span className="font-bold text-slate-900">{totalLabels}</span></div>
          <Button onClick={generate} disabled={loading || selected.length === 0} className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="generate-pdf"><Printer className="w-4 h-4 mr-2" />Generate PDF</Button>
        </div>
      </Card>
    </div>
  );
}
